import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { getCompensationMethod } from "@redotech/redo-customer-portal-shared/return-review/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ReturnTotalCalculations as Calculations,
  DraftReturn,
} from "@redotech/redo-model/draft-return/draft-return";
import { AdvancedExchangeItem } from "@redotech/redo-model/draft-return/draft-return-items";
import { getRelevantDisplayDiscountCodes } from "@redotech/redo-model/draft-return/util";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { CurrencyContext } from "@redotech/redo-web/currency";
import ModalCloseButton from "@redotech/redo-web/icon-old/modal-close-button.svg";
import TrashIcon from "@redotech/redo-web/icon-old/trash.svg";
import * as classNames from "classnames";
import { memo, useContext, useState } from "react";
import { createPortal } from "react-dom";
import * as review from "../review.module.css";
import * as advancedExchange from "./advanced-exchange.module.css";
import * as page from "./page.module.css";

const CartItem = memo(function CartItem({
  id,
  imageSrc,
  productTitle,
  variantTitle,
  quantity,
  price,
  itemValue,
  formatCurrency,
  removeItem,
  disabled = false,
}: {
  id: string;
  imageSrc: string;
  productTitle: string;
  variantTitle: string;
  quantity: number;
  price: string;
  itemValue?: string;
  formatCurrency: (amount: number) => string;
  removeItem: (id: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className={advancedExchange.cartItemContainer}>
      {disabled && <div className={advancedExchange.cartItemDisabled} />}
      <div className={advancedExchange.cartItem}>
        <div className={advancedExchange.cartProductImageContainer}>
          <img
            alt="product image"
            className={advancedExchange.cartProductImage}
            src={imageSrc}
          />
        </div>

        <div className={advancedExchange.cartItemContent}>
          <div className={advancedExchange.cartProductText}>
            <div className={advancedExchange.cartProductTitle}>
              {productTitle}
            </div>
            <div className={advancedExchange.cartProductVariantText}>
              {variantTitle}
            </div>
            <div className={advancedExchange.cartProductQuantity}>
              Quantity: {quantity}
            </div>
          </div>
          <div className={advancedExchange.cartItemLeft}>
            {itemValue && price !== itemValue && (
              <div className={classNames(review.price, review.voided)}>
                {formatCurrency(parseFloat(price))}
              </div>
            )}
            <div className={review.price}>
              {formatCurrency(parseFloat(itemValue ?? price))}
            </div>
            {!disabled && (
              <div
                className={advancedExchange.removeCartItemButton}
                onClick={() => removeItem(id)}
              >
                <div className={advancedExchange.deleteIconContainer}>
                  <TrashIcon />
                </div>{" "}
                Remove
              </div>
            )}
          </div>
        </div>
      </div>
      <hr className={advancedExchange.cartItemDivider} />
    </div>
  );
});

export const CartMenu = memo(function CartMenu({
  handleContinue,
  settings,
  merchantDashboard = false,
  draftReturn,
  client,
  open,
  setOpen,
  onItemRemove,
}: {
  handleContinue: () => void;
  settings: ReturnAppSettings;
  merchantDashboard?: boolean;
  draftReturn: DraftReturn;
  client: CustomerPortalRpcClient;
  open: boolean;
  setOpen: (open: boolean) => void;
  onItemRemove: (id: string) => void;
}) {
  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      setOpen(open);
    };

  const calculationsLoad = useLoad(async (): Promise<
    Calculations | undefined
  > => {
    if (!client) {
      return;
    }
    const response = await client.calculateReturnTotals({
      draftReturnId: draftReturn._id,
    });

    return response.calculations.processed;
  }, [client]);

  if (!calculationsLoad.value) {
    return null;
  }

  const compensationMethod = getCompensationMethod(draftReturn);

  return (
    <>
      <CartIconPortal
        draftReturn={draftReturn}
        setCartMenuOpen={setOpen}
        totalReturnableValue={compensationMethod.totalReturnableValue.toNumber()}
      />
      <div>
        <Drawer anchor="right" onClose={toggleDrawer(false)} open={open}>
          <ItemList
            calculations={calculationsLoad.value}
            draftReturn={draftReturn}
            handleContinue={handleContinue}
            merchantDashboard={merchantDashboard}
            newItems={draftReturn?.newOrderData?.items || []}
            removeItem={onItemRemove}
            settings={settings}
            toggleDrawer={toggleDrawer}
            totalReturnableValue={compensationMethod.totalReturnableValue.toNumber()}
          />
        </Drawer>
      </div>
    </>
  );
});

const ItemList = memo(function ItemList({
  toggleDrawer,
  merchantDashboard,
  newItems,
  removeItem,
  draftReturn,
  calculations,
  handleContinue,
  totalReturnableValue,
  settings,
}: {
  toggleDrawer: (
    open: boolean,
  ) => (event: React.KeyboardEvent | React.MouseEvent) => void;
  merchantDashboard?: boolean;
  newItems: AdvancedExchangeItem[];
  removeItem: (id: string) => void;
  draftReturn: DraftReturn;
  calculations: Calculations;
  handleContinue: () => void;
  totalReturnableValue: number;
  settings: ReturnAppSettings;
}) {
  const { formatCurrency } = useContext(CurrencyContext);
  const [isSaving, setIsSaving] = useState(false);
  const discountCodes = getRelevantDisplayDiscountCodes(draftReturn);

  const { paymentOwed } = calculations;

  return (
    <Box className={advancedExchange.cartMenu} role="presentation">
      <ModalCloseButton
        className={advancedExchange.cartExitButton}
        onClick={toggleDrawer(false)}
      />
      <Typography className={advancedExchange.cartMenuTitle} variant="h6">
        {merchantDashboard ? "Exchange Order" : "Your Cart"} (
        {newItems.length || 0})
      </Typography>
      <Divider />
      <div className={advancedExchange.cartProducts}>
        {newItems.map((lineItem, index) => (
          <CartItem
            disabled={false}
            formatCurrency={formatCurrency}
            id={lineItem.variantId}
            imageSrc={lineItem.images[0]}
            itemValue={lineItem?.itemValue ?? undefined}
            key={`${index}-${lineItem.variantId}`}
            price={lineItem.price}
            productTitle={lineItem.title}
            quantity={lineItem.quantity}
            removeItem={removeItem}
            variantTitle={lineItem.variantTitle}
          />
        ))}
        <div style={{ height: "200px" }} />
      </div>
      <div className={advancedExchange.cartContinueButtonContainer}>
        <Box
          className={classNames(
            advancedExchange.itemDetailsModalShadow,
            advancedExchange.cartShadowPosition,
          )}
        />

        {!merchantDashboard && (
          <>
            <Typography className={advancedExchange.cartTotalText}>
              {newItems.length > 0 ? (
                newItems.length === 1 ? (
                  <>New Item Price:</>
                ) : (
                  <>New Items Price:</>
                )
              ) : (
                <>New Item Price:</>
              )}{" "}
              {formatCurrency(
                parseFloat(draftReturn.newOrderData?.totalPrice || "0.0"),
              )}
            </Typography>
            {newItems.length > 0 && (
              <Typography className={advancedExchange.cartTotalText}>
                Taxes:{" "}
                {formatCurrency(
                  parseFloat(draftReturn.newOrderData?.totalTax || "0.0"),
                )}
              </Typography>
            )}
            {discountCodes &&
              discountCodes.length > 0 &&
              !settings?.exchanges?.blockDiscountsOnExchanges && (
                <Typography className={advancedExchange.cartTotalText}>
                  Discount codes: {discountCodes.join(", ")}
                </Typography>
              )}
            <Typography className={advancedExchange.cartAvailableStoreCredit}>
              {paymentOwed > 0 ? "Payment Total: " : "Store Credit Available: "}
              <span>
                {paymentOwed > 0
                  ? formatCurrency(paymentOwed)
                  : formatCurrency(totalReturnableValue)}
              </span>
            </Typography>
          </>
        )}

        <Button
          className={advancedExchange.cartContinueButton}
          disabled={
            merchantDashboard ||
            (!merchantDashboard &&
              (newItems.length ? newItems.length < 1 : true))
          }
          onClick={() => {
            if (merchantDashboard) {
              setIsSaving(true);
            }
            handleContinue();
          }}
          pending={isSaving}
          theme={ButtonTheme.PRIMARY}
        >
          {merchantDashboard ? "Save Changes" : "Continue"}
        </Button>
      </div>
    </Box>
  );
});

const CartIconPortal = memo(function CartIconPortal({
  totalReturnableValue,
  setCartMenuOpen,
  draftReturn,
}: {
  totalReturnableValue: number;
  setCartMenuOpen: (open: boolean) => void;
  draftReturn: DraftReturn;
}) {
  const { formatCurrency } = useContext(CurrencyContext);
  const size = useScreenType();

  const navBarCartIcon = document.getElementById("nav-bar-cart-icon-container");
  return navBarCartIcon
    ? createPortal(
        <>
          {size === ScreenType.DESKTOP && (
            <div
              className={page.cartContainer}
              onClick={() => {
                setCartMenuOpen(true);
              }}
            >
              {totalReturnableValue > 0 && (
                <div
                  className={classNames(page.cartTotalText, page.textSuccess)}
                >
                  {formatCurrency(totalReturnableValue)}
                </div>
              )}
              <div className={page.cartSizeBubble}>
                {draftReturn.newOrderItems.length || 0}
              </div>
              <svg
                className={page.cart}
                fill="none"
                height="24"
                stroke="#000000"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                width="24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10" cy="20.5" r="1" />
                <circle cx="18" cy="20.5" r="1" />
                <path d="M2.5 2.5h3l2.7 12.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6l1.6-8.4H7.1" />
              </svg>
            </div>
          )}
        </>,
        navBarCartIcon,
      )
    : null;
});
